import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent } from "react";

const AUTOSPIN_HOLD_MS = 500;
const PRESS_RELEASE_DELAY_MS = 200;

interface SpinButtonPressOptions {
    disabled: boolean;
    isBusy: boolean;
    canStartAutospin: boolean;
    isAutoSpinning: boolean;
    onSpin: () => void;
    onStartAutospin: () => void;
    onStopAutospin: () => void;
}

export function useSpinButtonPress(options: SpinButtonPressOptions) {
    const [isPressed, setIsPressed] = useState(false);
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const releaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const suppressClickRef = useRef(false);
    const pressingRef = useRef(false);

    const clearTimers = useCallback(() => {
        if (holdTimerRef.current !== null) clearTimeout(holdTimerRef.current);
        if (releaseTimerRef.current !== null)
            clearTimeout(releaseTimerRef.current);
        holdTimerRef.current = null;
        releaseTimerRef.current = null;
    }, []);

    const release = useCallback(() => {
        clearTimers();
        pressingRef.current = false;
        // Pointer-up precedes click. Keep the face pressed through that gap
        // while the click handler hands its visual state over to isBusy.
        releaseTimerRef.current = setTimeout(() => {
            releaseTimerRef.current = null;
            setIsPressed(false);
        }, PRESS_RELEASE_DELAY_MS);
    }, [clearTimers]);

    const cancel = useCallback(() => {
        if (pressingRef.current) suppressClickRef.current = true;
        clearTimers();
        pressingRef.current = false;
        setIsPressed(false);
    }, [clearTimers]);

    const begin = useCallback(() => {
        const current = optionsRef.current;
        if ((current.disabled || current.isBusy) && !current.isAutoSpinning)
            return;
        clearTimers();
        suppressClickRef.current = true;
        pressingRef.current = true;
        setIsPressed(true);
        if (current.isAutoSpinning) {
            current.onStopAutospin();
            return;
        }
        current.onSpin();
        if (!current.canStartAutospin) return;
        holdTimerRef.current = setTimeout(() => {
            holdTimerRef.current = null;
            const latest = optionsRef.current;
            if (latest.disabled || !pressingRef.current) return;
            suppressClickRef.current = true;
            latest.onStartAutospin();
        }, AUTOSPIN_HOLD_MS);
    }, [clearTimers]);

    useEffect(() => {
        if (options.disabled && !options.isAutoSpinning) cancel();
    }, [cancel, options.disabled, options.isAutoSpinning]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState !== "visible") cancel();
        };
        window.addEventListener("blur", cancel);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            clearTimers();
            window.removeEventListener("blur", cancel);
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
        };
    }, [cancel, clearTimers]);

    return {
        isPressed,
        onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
            if (event.isPrimary && event.button === 0) {
                begin();
            }
        },
        onPointerUp: release,
        onPointerLeave: cancel,
        onPointerCancel: cancel,
        onBlur: cancel,
        onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
            if (event.key !== " " && event.key !== "Enter") return;
            event.preventDefault();
            if (!event.repeat) begin();
        },
        onKeyUp: (event: KeyboardEvent<HTMLButtonElement>) => {
            if (event.key !== " " && event.key !== "Enter") return;
            event.preventDefault();
            release();
            event.currentTarget.click();
        },
        onClick: () => {
            release();
            if (suppressClickRef.current) {
                suppressClickRef.current = false;
                return;
            }
            const current = optionsRef.current;
            if (current.isAutoSpinning) current.onStopAutospin();
            else if (!current.disabled && !current.isBusy) current.onSpin();
        },
    };
}
